import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { BarcodeFormat, BrowserMultiFormatReader } from '@zxing/browser';
import type { IScannerControls } from '@zxing/browser';
import { DecodeHintType } from '@zxing/library';
import { useAuth } from '../../context';
import { giveToGuest, extractCidFromQrCode } from '../../api/sharing';
import { Button, ActivityIndicator } from '../../components/ui';
import { images } from '../../config/colors';
import tocBackgroundFallback from '../../assets/tocBackground.jpg';
import styles from './GiveCoinsPage.module.css';
import type { Account, EventData } from '../../api/event';

const tocBackground = images.background || tocBackgroundFallback;

type Step = 'amount' | 'scan' | 'result';

interface LocationState {
  account?: Account;
  event?: EventData;
  maxCoins?: number;
}

export function GiveCoinsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { loginKey } = useAuth();
  const { account, event, maxCoins = 0 } = (location.state as LocationState) || {};

  const [step, setStep] = useState<Step>('amount');
  const [amount, setAmount] = useState<number | ''>('');
  const [loading, setLoading] = useState(false);
  const [resultSuccess, setResultSuccess] = useState(false);
  const [resultMessage, setResultMessage] = useState('');
  const [cameraError, setCameraError] = useState('');

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const controlsRef = useRef<IScannerControls | null>(null);

  const maxAmount = Math.min(100, maxCoins);

  const cleanupCamera = useCallback(() => {
    controlsRef.current?.stop();
    controlsRef.current = null;
    streamRef.current?.getTracks().forEach((track) => {
      if (track.readyState === 'live') {
        track.stop();
        track.enabled = false;
      }
    });
    streamRef.current = null;
  }, []);

  const handleScan = useCallback(async (qrCode: string) => {
    if (!loginKey || !event || !account || loading) return;
    if (typeof amount !== 'number') return;

    const destinationCid = extractCidFromQrCode(qrCode);
    if (!destinationCid) {
      setResultSuccess(false);
      setResultMessage('Invalid QR code. Please scan a valid guest QR code.');
      setStep('result');
      cleanupCamera();
      return;
    }

    setLoading(true);
    cleanupCamera();

    const response = await giveToGuest(loginKey, account.id, destinationCid, amount);

    setLoading(false);

    if (response.ok && response.data?.success) {
      setResultSuccess(true);
      setResultMessage(`Successfully gave ${amount} coin${amount !== 1 ? 's' : ''}!`);
    } else {
      const errorMsg = response.data?.error?.message || 'Transfer failed. Please try again.';
      setResultSuccess(false);
      setResultMessage(errorMsg);
    }

    setStep('result');
  }, [loginKey, event, account, amount, loading, cleanupCamera]);

  useEffect(() => {
    if (step !== 'scan') return;

    let cancelled = false;

    const startScanning = async () => {
      try {
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            width: { max: 1920 },
            height: { max: 1920 },
            facingMode: { ideal: 'environment' },
          },
        });

        if (cancelled) {
          mediaStream.getTracks().forEach((t) => t.stop());
          return;
        }

        streamRef.current = mediaStream;
        const videoTracks = mediaStream.getVideoTracks();
        const track = videoTracks[0];
        const deviceId = track.getSettings().deviceId;

        if (deviceId && videoRef.current) {
          const hints = new Map();
          hints.set(DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.QR_CODE]);

          const reader = new BrowserMultiFormatReader();
          reader.setHints(hints);

          const controls = await reader.decodeFromVideoDevice(
            deviceId,
            videoRef.current,
            (result, error) => {
              if (!error && result && !cancelled) {
                handleScan(result.getText());
              }
            },
          );

          if (cancelled) {
            controls.stop();
          } else {
            controlsRef.current = controls;
          }
        }
      } catch (err: unknown) {
        if (!cancelled) {
          const error = err as Error;
          if (error.name === 'NotAllowedError' || error.name === 'SecurityError') {
            setCameraError('Camera access denied. Please allow camera permissions and try again.');
          } else {
            setCameraError('Could not access camera. Please check your device settings.');
          }
        }
      }
    };

    startScanning();

    return () => {
      cancelled = true;
      cleanupCamera();
    };
  }, [step, cleanupCamera, handleScan]);

  const handleNext = () => {
    if (typeof amount === 'number' && amount >= 1 && amount <= maxAmount) {
      setCameraError('');
      setStep('scan');
    }
  };

  const handleScanAgain = () => {
    setResultMessage('');
    setResultSuccess(false);
    setCameraError('');
    setStep('scan');
  };

  return (
    <div
      className={styles.background}
      style={{ backgroundImage: `url(${tocBackground})` }}
    >
      <ActivityIndicator visible={loading} />
      <div className={styles.container}>
        {step === 'amount' && (
          <>
            <p className={styles.title}>Give Coins</p>
            <p className={styles.subtitle}>
              How many coins would you like to give? (max {maxAmount})
            </p>
            <input
              type="number"
              className={styles.amountInput}
              min={1}
              max={maxAmount}
              value={amount}
              onFocus={(e) => e.target.select()}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === '') {
                  setAmount('');
                  return;
                }
                const val = parseInt(raw, 10);
                if (!isNaN(val)) setAmount(Math.max(1, Math.min(maxAmount, val)));
              }}
            />
            <Button onClick={handleNext}>Next</Button>
            <Button theme="outline" onClick={() => navigate(-1)}>Cancel</Button>
          </>
        )}

        {step === 'scan' && (
          <>
            <p className={styles.title}>Scan Recipient&apos;s QR Code</p>
            <p className={styles.subtitle}>
              Giving {amount} coin{amount !== 1 ? 's' : ''}
            </p>
            <div className={styles.videoContainer}>
              <video ref={videoRef} autoPlay className={styles.video} />
            </div>
            {cameraError && <p className={styles.cameraError}>{cameraError}</p>}
            <Button theme="outline" onClick={() => { cleanupCamera(); navigate(-1); }}>Cancel</Button>
          </>
        )}

        {step === 'result' && (
          <>
            <p className={styles.title}>{resultSuccess ? 'Success!' : 'Error'}</p>
            <p className={`${styles.resultMessage} ${resultSuccess ? styles.success : styles.error}`}>
              {resultMessage}
            </p>
            <div className={styles.buttonRow}>
              <Button onClick={() => navigate('/', { replace: true })}>Done</Button>
              {!resultSuccess && (
                <Button theme="outline" onClick={handleScanAgain}>Try Again</Button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default GiveCoinsPage;
